import { extractBackendError } from '@/lib/translateBackendError';

/** The backend code a 409 optimistic-lock rejection carries. */
export const CONCURRENT_MODIFICATION = 'CONCURRENT_MODIFICATION';

/** The backend code a 422 derived-status rejection carries (issue #1224). */
export const ENTITY_STATUS_DERIVED = 'ENTITY_STATUS_DERIVED';

/** isConcurrentModification reports whether a rejection is the 409 optimistic-lock case. */
export function isConcurrentModification(error: unknown): boolean {
  return extractBackendError(error).code === CONCURRENT_MODIFICATION;
}

/** isEntityStatusDerived reports whether a rejection is the 422 derived-status case. */
export function isEntityStatusDerived(error: unknown): boolean {
  return extractBackendError(error).code === ENTITY_STATUS_DERIVED;
}

/**
 * retryOnConcurrentModification runs `mutate` and, on a rejection carrying
 * `CONCURRENT_MODIFICATION`, runs it exactly ONCE more before rethrowing.
 *
 * Why one plain retry is enough (issue #1284, decision D5): every update handler
 * re-reads the row and takes its `updated_at` INSIDE the same request, so the
 * second attempt starts from a fresh optimistic-lock baseline — no form reload
 * is needed. And a derived entity's payload no longer carries `status` at all
 * (issue #1224), so the retry cannot overwrite what the derivation job just
 * wrote. If the second attempt also loses the race, the error is rethrown and
 * the caller shows the translated message; there is no third attempt.
 *
 * What the retry does NOT buy: it re-sends the SAME payload, and both edit forms
 * send the whole validated snapshot. A non-`status` field the admin loaded stale
 * is therefore overwritten by the first attempt already — the retry neither adds
 * nor removes that exposure. The lock's baseline is per request, not per form
 * load. See docs/guides/backend-error-codes-i18n.md, "The 409 guarantee is per
 * REQUEST, not per form load".
 *
 * A 422 `ENTITY_STATUS_DERIVED` is NEVER retried (decision D10). The retry would
 * re-send the identical payload, so an entity that gained a window between the
 * two attempts could only answer 422 again. It rethrows at once, and the caller
 * translates it and refetches so the status control re-renders read-only.
 */
export async function retryOnConcurrentModification<T>(
  mutate: () => Promise<T>
): Promise<T> {
  try {
    return await mutate();
  } catch (error) {
    if (!isConcurrentModification(error)) {
      throw error;
    }
    return await mutate();
  }
}
